// FICHA DEL NEGOCIO (admin, reorden F2, 24-oct-2026). Una sola pantalla por negocio con lo que antes se buscaba en
// cuatro: ¿está vivo? (último login, visitas y canjes a 30 d), su suscripción (editable aquí mismo), sus pagos, sus
// módulos y POS, su línea de WhatsApp, su gente, y los botones de «Entrar como». Medido en GA4: ese recorrido era
// el 89 % del uso del admin, casi todo desde el celular.
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import NumInput from '../../../components/ui/NumInput';
import { useNavigate, useParams, Link as RouterLink } from 'react-router-dom';
import {
  ArrowLeft, Store, Crown, CreditCard, SlidersHorizontal, Phone, Users, Eye, ExternalLink, ClipboardCheck,
  Activity, Calendar, MessageSquare, RefreshCw, Pencil, Check, X,
} from 'lucide-react';
import Layout from '../../../components/layout/Layout';
import BrandLoader from '../../../components/common/BrandLoader';
import ConfirmDialog from '../../../components/common/ConfirmDialog';
import { VerticalBadge } from '../../../components/admin/VerticalBadge';
import { useError } from '../../../context/ErrorContext';
import { adminNegocioApi, type ModuloRow, type PagoRaw, type PosInfo, type Resumen, type SuscripcionRaw, type TenantRow, type UsuarioRow, type WhatsAppInfo } from '../../../api/adminNegocioApi';
import { impersonateUser, impersonationCode } from '../../../api/adminUsersApi';
import { billingApi, type BillingPeriod } from '../../../api/billingApi';
import { UsoDetalle, useUsoNegocios } from '../components/UsoNegocio';

const fecha = (v: string | null | undefined, conHora = false): string => {
  if (!v) return '—';
  const d = new Date(v);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric', ...(conHora ? { hour: '2-digit', minute: '2-digit' } : {}) });
};
const hace = (v: string | null | undefined): string => {
  if (!v) return 'nunca';
  const ms = Date.now() - new Date(v).getTime();
  if (isNaN(ms)) return '—';
  const d = Math.floor(ms / 86400000);
  if (d <= 0) return 'hoy';
  if (d === 1) return 'ayer';
  if (d < 30) return `hace ${d} días`;
  if (d < 365) return `hace ${Math.floor(d / 30)} meses`;
  return `hace ${Math.floor(d / 365)} años`;
};
const dinero = (n: number | string | null | undefined): string => {
  const x = Number(n);
  return Number.isFinite(x) ? `$${new Intl.NumberFormat('es-MX', { maximumFractionDigits: 0 }).format(x)}` : '—';
};
const PLAN_LABEL: Record<string, string> = { free: 'Gratis', pro: 'PRO', pro_monthly: 'PRO mensual', pro_semiannual: 'PRO semestral', pro_yearly: 'PRO anual', premium: 'Premium (legacy)', onboarding: 'Onboarding' };
const periodoDe = (code: string): BillingPeriod => (code === 'pro_monthly' ? 'monthly' : code === 'pro_semiannual' ? 'semiannual' : 'yearly');
const ymd = (v: string | null | undefined): string => {
  if (!v) return '';
  const d = new Date(v);
  if (isNaN(d.getTime())) return '';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

const Tarjeta: React.FC<{ titulo: string; icono: React.ElementType; accion?: React.ReactNode; children: React.ReactNode; testid?: string }> = ({ titulo, icono: Icono, accion, children, testid }) => (
  <section className="bg-surface rounded-lg shadow p-4 sm:p-5" data-testid={testid}>
    <div className="flex items-center justify-between gap-2 mb-3">
      <h2 className="text-sm font-bold uppercase tracking-wider text-content-3 flex items-center"><Icono className="h-4 w-4 mr-2 text-accent" /> {titulo}</h2>
      {accion}
    </div>
    {children}
  </section>
);

const Kpi: React.FC<{ etiqueta: string; valor: React.ReactNode; nota?: string; alerta?: boolean }> = ({ etiqueta, valor, nota, alerta }) => (
  <div className={`rounded-lg border p-3 ${alerta ? 'border-amber-300 bg-amber-50 dark:bg-amber-500/10 dark:border-amber-500/40' : 'border-line bg-surface'}`}>
    <p className="text-[11px] uppercase tracking-wide text-content-3">{etiqueta}</p>
    <p className="text-xl font-display font-extrabold text-content mt-0.5">{valor}</p>
    {nota && <p className="text-[11px] text-content-3 mt-0.5">{nota}</p>}
  </div>
);

const AdminNegocioPage: React.FC = () => {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { addError } = useError();
  const [cargando, setCargando] = useState(true);
  const [tenant, setTenant] = useState<TenantRow | null>(null);
  const [modulos, setModulos] = useState<ModuloRow[]>([]);
  const [usuarios, setUsuarios] = useState<UsuarioRow[]>([]);
  const [sub, setSub] = useState<SuscripcionRaw | null>(null);
  const [pagos, setPagos] = useState<PagoRaw[]>([]);
  const [resumen, setResumen] = useState<Resumen | null>(null);
  // «Cómo lo usan» (admin F3): el mismo detalle que el popover de las listas, completo.
  const idsUso = useMemo(() => (id ? [id] : []), [id]);
  const { uso: usoMapa, cargando: usoCargando } = useUsoNegocios(idsUso);
  const usoNegocio = usoMapa[String(id)];
  const [wa, setWa] = useState<WhatsAppInfo | null>(null);
  const [pos, setPos] = useState<PosInfo | null>(null);
  const [noExiste, setNoExiste] = useState(false);

  // Editor de suscripción (los mismos campos que Suscripciones; guarda por PUT /tenants/:id/subscription action=set)
  const [editando, setEditando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [fPlan, setFPlan] = useState<string>('free');
  const [fVence, setFVence] = useState('');
  const [fCortesia, setFCortesia] = useState(false);
  const [fSucursales, setFSucursales] = useState(1);
  const [fPrecio, setFPrecio] = useState('');
  const [aviso, setAviso] = useState<string | null>(null);

  const [verComo, setVerComo] = useState<UsuarioRow | null>(null);
  const [verComoBusy, setVerComoBusy] = useState(false);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const t = await adminNegocioApi.tenant(id);
      setTenant(t.tenant); setModulos(t.modules || []); setUsuarios(t.users || []);
      const [s, r, w, p] = await Promise.allSettled([
        adminNegocioApi.suscripcion(id), adminNegocioApi.resumen(id), adminNegocioApi.whatsapp(id), adminNegocioApi.pos(id),
      ]);
      if (s.status === 'fulfilled') { setSub(s.value.subscription); setPagos(s.value.payments || []); }
      if (r.status === 'fulfilled') setResumen(r.value);
      if (w.status === 'fulfilled') setWa(w.value);
      if (p.status === 'fulfilled') setPos(p.value);
    } catch (e) {
      const msg = (e as Error).message || '';
      if (/404|No existe/i.test(msg)) setNoExiste(true); else addError(msg || 'No se pudo cargar el negocio');
    } finally { setCargando(false); }
  }, [id, addError]);
  useEffect(() => { void cargar(); }, [cargar]);

  const dueno = useMemo(() => usuarios.find((u) => u.role === 'owner' && u.is_active) || usuarios.find((u) => u.role === 'owner') || null, [usuarios]);
  const vertical = tenant?.vertical || 'lealtad';
  const esLealtad = vertical === 'lealtad';
  const modulosActivos = modulos.filter((m) => m.applies && m.enabled && (m.entitled || m.is_core));
  const waEstado = (() => {
    const st = wa?.status as Record<string, unknown> | null;
    const v = st ? (st.status ?? st.state ?? st.live_status) : null;
    return v ? String(v) : (wa?.config ? 'sin estado' : 'sin línea propia');
  })();

  const abrirEditor = () => {
    if (!sub) return;
    setFPlan(sub.plan_code && sub.plan_code !== 'free' ? sub.plan_code : 'free');
    setFVence(ymd(sub.expires_at));
    setFCortesia(!!sub.is_trial);
    setFSucursales(Math.max(1, Number(sub.max_branches) || 1));
    setFPrecio(sub.custom_price != null ? String(sub.custom_price) : '');
    setAviso(null);
    setEditando(true);
  };
  const guardarSub = async () => {
    const pago = fPlan !== 'free';
    if (pago && !fVence) { setAviso('Falta la fecha de vencimiento.'); return; }
    let endDate: Date | null = null;
    if (pago) {
      const [y, m, d] = fVence.split('-').map(Number);
      endDate = new Date(); endDate.setFullYear(y, m - 1, d); endDate.setHours(23, 59, 59, 999);
      if (isNaN(endDate.getTime())) { setAviso('Fecha inválida.'); return; }
    }
    let customPrice: number | null = null;
    if (pago && fPrecio.trim() !== '') {
      const n = Number(fPrecio.replace(/[^0-9.]/g, ''));
      if (!Number.isFinite(n) || n < 0) { setAviso('Precio acordado inválido.'); return; }
      customPrice = n;
    }
    setGuardando(true); setAviso(null);
    try {
      await billingApi.updateSubscription(id, {
        plan: pago ? 'pro' : 'free',
        billingPeriod: pago ? periodoDe(fPlan) : undefined,
        endDate, status: 'active',
        paymentMethod: pago ? (fCortesia ? 'cortesia' : (sub?.payment_method || 'manual')) : null,
        isTrial: pago ? fCortesia : false,
        maxBranches: Math.max(1, Math.floor(fSucursales)),
        customPrice,
      });
      setEditando(false);
      const s = await adminNegocioApi.suscripcion(id);
      setSub(s.subscription); setPagos(s.payments || []);
    } catch (e) {
      setAviso((e as Error).message || 'No se pudo guardar');
    } finally { setGuardando(false); }
  };

  if (cargando) return <Layout><BrandLoader label="Cargando la ficha…" /></Layout>;
  if (noExiste || !tenant) {
    return (
      <Layout>
        <div className="bg-surface rounded-lg shadow p-6 text-center">
          <p className="text-content">Ese negocio no existe.</p>
          <RouterLink to="/dashboard/admin/users" className="text-accent hover:underline text-sm">← Volver a Negocios</RouterLink>
        </div>
      </Layout>
    );
  }

  const diasSinLogin = resumen?.ultimo_login ? Math.floor((Date.now() - new Date(resumen.ultimo_login).getTime()) / 86400000) : null;
  const estadoChip = tenant.status === 'archived'
    ? 'bg-gray-200 text-gray-700 dark:bg-gray-500/20 dark:text-gray-300'
    : sub?.is_pro ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-500/20 dark:text-yellow-300' : 'bg-green-100 text-green-800 dark:bg-green-500/20 dark:text-green-300';

  return (
    <Layout>
      <div className="space-y-5" data-testid="negocio-ficha-pagina">
        {/* Encabezado */}
        {/* En celular las acciones bajan a su propia línea: si no, el nombre quedaba en «M…». */}
        <div className="flex flex-wrap items-start gap-3 animate-fade-in-up">
          <button onClick={() => navigate('/dashboard/admin/users')} title="Volver a Negocios" className="p-2 rounded-md hover:bg-surface-2 text-content-2 mt-0.5"><ArrowLeft className="h-5 w-5" /></button>
          <div className="min-w-0 flex-1 basis-48">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-xl sm:text-2xl font-bold text-content flex items-center min-w-0"><Store className="h-6 w-6 mr-2 text-accent flex-shrink-0" /><span className="break-words" data-testid="negocio-nombre">{tenant.name}</span></h1>
              <VerticalBadge vertical={vertical} />
              <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${estadoChip}`} data-testid="negocio-plan">
                {tenant.status === 'archived' ? 'Archivado' : PLAN_LABEL[sub?.plan_code || 'free'] || sub?.plan_code}
                {sub?.is_trial ? ' · cortesía' : ''}
              </span>
            </div>
            <p className="text-sm text-content-2 mt-1">
              #{tenant.id} · {tenant.slug}{tenant.custom_domain ? ` · ${tenant.custom_domain}` : ''} · alta {fecha(tenant.created_at)} · {tenant.timezone || 'sin zona horaria'}
            </p>
          </div>
          <div className="flex items-center justify-end gap-2 w-full sm:w-auto">
            <button onClick={() => void cargar()} title="Actualizar" className="p-2 rounded-md border border-line text-content-2 hover:bg-surface-2"><RefreshCw className="h-4 w-4" /></button>
            {dueno && (
              <button
                type="button"
                onClick={() => setVerComo(dueno)}
                data-testid="negocio-entrar-como"
                className="inline-flex items-center px-3 py-2 rounded-md text-sm font-medium text-white bg-accent hover:opacity-90"
              >
                <Eye className="h-4 w-4 mr-1.5" /> Entrar como {dueno.name ? dueno.name.split(' ')[0] : 'el dueño'}
                {!esLealtad && <ExternalLink className="h-3 w-3 ml-1.5 opacity-70" />}
              </button>
            )}
          </div>
        </div>

        {/* ¿Está vivo? */}
        <div className="grid grid-cols-2 lg:grid-cols-6 gap-3 animate-fade-in-up" data-testid="negocio-kpis">
          <Kpi etiqueta="Último login" valor={hace(resumen?.ultimo_login)} nota={fecha(resumen?.ultimo_login, true)} alerta={diasSinLogin != null && diasSinLogin > 30} />
          <Kpi etiqueta="Clientes" valor={resumen ? resumen.contactos.activos.toLocaleString('es-MX') : '—'} nota={resumen ? `${resumen.contactos.total.toLocaleString('es-MX')} en total · último ${hace(resumen.contactos.ultimo_alta)}` : undefined} />
          <Kpi etiqueta="Visitas 30 d" valor={resumen ? resumen.visitas.d30.toLocaleString('es-MX') : '—'} nota={resumen ? `última ${hace(resumen.visitas.ultima)}` : undefined} alerta={!!resumen && resumen.visitas.d30 === 0 && esLealtad} />
          <Kpi etiqueta="Canjes 30 d" valor={resumen ? resumen.canjes.d30.toLocaleString('es-MX') : '—'} nota={resumen ? `último ${hace(resumen.canjes.ultimo)}` : undefined} />
          <Kpi etiqueta="WhatsApp 30 d" valor={resumen ? `${resumen.whatsapp_30d.ok}/${resumen.whatsapp_30d.enviados}` : '—'} nota="entregados / intentos" alerta={!!resumen && resumen.whatsapp_30d.enviados > 0 && resumen.whatsapp_30d.ok < resumen.whatsapp_30d.enviados * 0.8} />
          <Kpi etiqueta="Campañas 30 d" valor={resumen ? resumen.campanas_30d.n : '—'} nota={resumen ? `última ${hace(resumen.campanas_30d.ultima)}` : undefined} />
        </div>

        <div className="grid gap-5 lg:grid-cols-2 animate-fade-in-up">
          {/* Suscripción */}
          <Tarjeta titulo="Suscripción" icono={Crown} testid="negocio-suscripcion" accion={
            !editando ? (
              <button type="button" onClick={abrirEditor} data-testid="negocio-sub-editar" className="inline-flex items-center text-xs font-medium text-accent hover:underline"><Pencil className="h-3.5 w-3.5 mr-1" /> Editar</button>
            ) : null
          }>
            {!sub ? <p className="text-sm text-content-3">Sin datos de suscripción.</p> : !editando ? (
              <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
                <dt className="text-content-3">Plan</dt><dd className="text-content font-medium">{PLAN_LABEL[sub.plan_code] || sub.plan_code}{sub.is_trial ? ' (cortesía)' : ''}</dd>
                <dt className="text-content-3">Estado</dt><dd className="text-content">{sub.status}{sub.expired ? ' · vencida' : ''}</dd>
                <dt className="text-content-3">Vence</dt><dd className={`font-medium ${sub.days_left != null && sub.days_left <= 7 ? 'text-amber-600 dark:text-amber-400' : 'text-content'}`}>{fecha(sub.expires_at)}{sub.days_left != null ? ` (${sub.days_left} días)` : ''}</dd>
                <dt className="text-content-3">Sucursales</dt><dd className="text-content">{sub.max_branches}</dd>
                <dt className="text-content-3">Precio</dt><dd className="text-content">{sub.custom_price != null ? `${dinero(sub.custom_price)} acordado` : `${dinero(sub.price)} tarifa`}</dd>
                <dt className="text-content-3">Cobro</dt><dd className="text-content">{sub.payment_method || '—'}{sub.autopay_status ? ` · domiciliación ${sub.autopay_status}` : ''}</dd>
              </dl>
            ) : (
              <div className="space-y-3 text-sm" data-testid="negocio-sub-form">
                <div className="grid grid-cols-2 gap-3">
                  <label className="block"><span className="text-xs text-content-3">Plan</span>
                    <select value={fPlan} onChange={(e) => setFPlan(e.target.value)} className="mt-1 w-full rounded-md border border-line bg-canvas px-2 py-1.5 text-content" data-testid="negocio-sub-plan">
                      <option value="free">Gratis</option><option value="pro_monthly">PRO mensual</option><option value="pro_semiannual">PRO semestral</option><option value="pro_yearly">PRO anual</option>
                    </select></label>
                  <label className="block"><span className="text-xs text-content-3">Vence</span>
                    <input type="date" value={fVence} onChange={(e) => setFVence(e.target.value)} disabled={fPlan === 'free'} className="mt-1 w-full rounded-md border border-line bg-canvas px-2 py-1.5 text-content disabled:opacity-50" data-testid="negocio-sub-vence" /></label>
                  <label className="block"><span className="text-xs text-content-3">Sucursales</span>
                    <NumInput min={1} value={fSucursales} emptyAs={1} onValue={(n) => setFSucursales(n || 1)} className="mt-1 w-full rounded-md border border-line bg-canvas px-2 py-1.5 text-content" /></label>
                  <label className="block"><span className="text-xs text-content-3">Precio acordado (vacío = tarifa)</span>
                    <input type="text" inputMode="decimal" value={fPrecio} onChange={(e) => setFPrecio(e.target.value)} disabled={fPlan === 'free'} placeholder="tarifa" className="mt-1 w-full rounded-md border border-line bg-canvas px-2 py-1.5 text-content disabled:opacity-50" /></label>
                </div>
                <label className="inline-flex items-center gap-2 text-content-2"><input type="checkbox" checked={fCortesia} onChange={(e) => setFCortesia(e.target.checked)} disabled={fPlan === 'free'} /> Cortesía (prueba sin cobro)</label>
                {aviso && <p className="text-xs text-red-600 dark:text-red-400" data-testid="negocio-sub-aviso">{aviso}</p>}
                <div className="flex gap-2">
                  <button type="button" onClick={() => void guardarSub()} disabled={guardando} data-testid="negocio-sub-guardar" className="inline-flex items-center px-3 py-1.5 rounded-md text-sm font-medium text-white bg-accent hover:opacity-90 disabled:opacity-50"><Check className="h-4 w-4 mr-1" /> {guardando ? 'Guardando…' : 'Guardar'}</button>
                  <button type="button" onClick={() => setEditando(false)} disabled={guardando} className="inline-flex items-center px-3 py-1.5 rounded-md text-sm text-content-2 border border-line hover:bg-surface-2"><X className="h-4 w-4 mr-1" /> Cancelar</button>
                </div>
              </div>
            )}
          </Tarjeta>

          {/* Pagos */}
          <Tarjeta titulo="Pagos" icono={CreditCard} testid="negocio-pagos" accion={<RouterLink to="/dashboard/admin/payments" className="text-xs text-accent hover:underline">Todos los pagos →</RouterLink>}>
            {pagos.length === 0 ? <p className="text-sm text-content-3">Sin pagos registrados.</p> : (
              <ul className="divide-y divide-line text-sm">
                {pagos.slice(0, 8).map((p) => (
                  <li key={p.id} className="py-1.5 flex items-center justify-between gap-2">
                    <span className="text-content-2 flex items-center min-w-0"><Calendar className="h-3.5 w-3.5 mr-1.5 text-content-3 flex-shrink-0" />{fecha(p.created_at)} · <span className="ml-1 truncate">{PLAN_LABEL[p.plan_code || ''] || p.plan_code || '—'} · {p.method}</span></span>
                    <span className="flex items-center gap-2 flex-shrink-0">
                      <span className="font-medium text-content">{dinero(p.amount)}</span>
                      <span className={`px-1.5 py-0.5 rounded text-[11px] font-semibold ${p.status === 'completed' ? 'bg-green-100 text-green-800 dark:bg-green-500/20 dark:text-green-300' : p.status === 'pending' ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-500/20 dark:text-yellow-300' : 'bg-gray-100 text-gray-700 dark:bg-gray-500/20 dark:text-gray-300'}`}>{p.status}</span>
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Tarjeta>

          {/* Módulos y POS */}
          <Tarjeta titulo="Módulos y POS" icono={SlidersHorizontal} testid="negocio-modulos" accion={<RouterLink to={`/dashboard/admin/tenants/${tenant.id}/modules`} className="text-xs text-accent hover:underline" data-testid="negocio-modulos-link">Configurar →</RouterLink>}>
            <div className="flex flex-wrap gap-1.5">
              {modulosActivos.length === 0 && <span className="text-sm text-content-3">Sin módulos activos.</span>}
              {modulosActivos.map((m) => (
                <span key={m.module_key} className="px-2 py-0.5 rounded-full text-xs bg-surface-2 text-content-2 border border-line">{m.name}</span>
              ))}
            </div>
            <p className="text-xs text-content-3 mt-3 flex items-center">
              <Activity className="h-3.5 w-3.5 mr-1" />
              {pos?.linked ? `Punto de venta enlazado (POS #${pos.pos_tenant_id}${pos.available === false ? ', sin respuesta' : ''})` : 'Sin punto de venta'}
            </p>
          </Tarjeta>

          {/* WhatsApp */}
          <Tarjeta titulo="WhatsApp del negocio" icono={Phone} testid="negocio-whatsapp" accion={<RouterLink to="/dashboard/admin/wa-health" className="text-xs text-accent hover:underline">Salud WhatsApp →</RouterLink>}>
            <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
              <dt className="text-content-3">Línea</dt><dd className="text-content">{wa?.config ? `${wa.config.session} (${wa.config.base})` : 'compartida (sin línea propia)'}</dd>
              <dt className="text-content-3">Estado</dt><dd className="text-content">{waEstado}</dd>
              <dt className="text-content-3">Últimos 30 días</dt><dd className="text-content">{resumen ? `${resumen.whatsapp_30d.ok} entregados de ${resumen.whatsapp_30d.enviados} · último ${hace(resumen.whatsapp_30d.ultimo)}` : '—'}</dd>
            </dl>
          </Tarjeta>

          {/* Gente */}
          <Tarjeta titulo="Quién entra" icono={Users} testid="negocio-usuarios">
            {usuarios.length === 0 ? <p className="text-sm text-content-3">Sin usuarios.</p> : (
              <ul className="divide-y divide-line text-sm">
                {usuarios.map((u) => (
                  <li key={u.id} className="py-1.5 flex items-center justify-between gap-2">
                    <span className="min-w-0"><span className="font-medium text-content">{u.name || '—'}</span> <span className="text-content-3 truncate">· {u.email}</span></span>
                    <span className="flex items-center gap-2 flex-shrink-0">
                      <span className="px-1.5 py-0.5 rounded text-[11px] bg-surface-2 text-content-2 border border-line">{u.role}</span>
                      {!u.is_active && <span className="text-[11px] text-content-3">inactivo</span>}
                      {u.role === 'owner' && u.is_active ? null : (
                        <button type="button" onClick={() => setVerComo(u)} title={`Entrar como ${u.name || u.email}`} className="p-1 rounded text-content-3 hover:text-accent"><Eye className="h-4 w-4" /></button>
                      )}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </Tarjeta>

          {/* Cómo lo usan (admin F3): último rastro, quiénes regresan, actividad por giro, riesgo de renovación */}
          <Tarjeta titulo="Cómo lo usan" icono={Activity} testid="negocio-uso">
            {usoNegocio ? (
              <UsoDetalle uso={usoNegocio} tenantId={id} />
            ) : (
              <p className="text-sm text-content-3">{usoCargando ? 'Midiendo…' : 'Sin datos de uso.'}</p>
            )}
          </Tarjeta>

          {/* Adopción */}
          <Tarjeta titulo="Adopción de funciones" icono={ClipboardCheck} testid="negocio-adopcion">
            <p className="text-sm text-content-2">
              Qué funciones automáticas usa de verdad este negocio (avisos, campañas, cumpleaños, mensajes de lealtad) se ve en
              <RouterLink to="/dashboard/admin/adopcion" className="text-accent hover:underline ml-1">Adopción</RouterLink>, con todos los de pago.
            </p>
            {resumen && (
              <p className="text-xs text-content-3 mt-2 flex items-center"><MessageSquare className="h-3.5 w-3.5 mr-1" /> Aquí: {resumen.campanas_30d.n} campañas y {resumen.whatsapp_30d.enviados} WhatsApp en 30 días.</p>
            )}
          </Tarjeta>
        </div>

        <ConfirmDialog
          open={!!verComo}
          title={`¿Entrar como ${verComo?.name || verComo?.email || ''}?`}
          message={<>{esLealtad ? 'El panel se recargará como ese usuario.' : 'Se abrirá su panel en una pestaña nueva.'}<br />Las acciones que hagas quedarán a su nombre.</>}
          confirmLabel="Entrar como"
          busy={verComoBusy}
          onCancel={() => setVerComo(null)}
          onConfirm={async () => {
            if (!verComo) return;
            setVerComoBusy(true);
            try {
              if (esLealtad) {
                await impersonateUser(verComo.id);
                window.location.href = '/';
              } else {
                const win = window.open('', '_blank');
                try {
                  const { url } = await impersonationCode(verComo.id);
                  if (win) win.location.href = url; else window.open(url, '_blank');
                } catch (e) { win?.close(); throw e; }
                setVerComoBusy(false); setVerComo(null);
              }
            } catch (e) {
              addError((e as Error).message || 'No se pudo entrar como ese usuario');
              setVerComoBusy(false); setVerComo(null);
            }
          }}
        />
      </div>
    </Layout>
  );
};

export default AdminNegocioPage;
